import { ItemDropTarget, ItemDetailMeta, ItemDetailOption } from "game-common/models"
import { Container, Graphics, InteractionEvent, InteractionManager, Point, Rectangle } from "pixi.js"

interface Slot {
    node: Graphics
    item?: ItemDetailMeta
    section: ItemDropTarget
    size: number
    highlight: (on: boolean) => void
}

interface Section {
    node: Container
    bounds: Rectangle
    highlighted: boolean
    section: ItemDropTarget
    highlight: (on: boolean) => void
}

/** Dispatch only supplied actions; inventory dialogs supply server-minted options. */
export class ItemSlotDrag {
    private slots: Slot[] = []
    private sections: Section[] = []
    private held?: { source: Slot; pointerId: number; start: Point; moved: boolean }
    private ghost?: Container
    private suppressed?: Graphics

    constructor(
        private interaction: InteractionManager,
        private layer: Container,
        private makeGhost: (item: ItemDetailMeta, size: number) => Container,
        // `at`: the screen point it was let go of, given when it was let go of over the world.
        private select: (item: ItemDetailMeta, option: ItemDetailOption, at?: Point) => void,
        private hideDetails: () => void,
        private isWorldDrop: (point: Point) => boolean = () => false,
    ) {}

    registerSection = (node: Container, bounds: Rectangle, section: ItemDropTarget,
        highlight: (on: boolean) => void) => {
        this.sections.push({ node, bounds, section, highlight, highlighted: false })
    }

    registerTarget = (node: Graphics, item: ItemDetailMeta | undefined, section: ItemDropTarget,
        size: number, highlight: (on: boolean) => void) => {
        const target = { node, item, section, size, highlight }
        this.slots.push(target)
        return target
    }

    register = (node: Graphics, item: ItemDetailMeta | undefined, section: ItemDropTarget,
        size: number, highlight: (on: boolean) => void) => {
        const source = this.registerTarget(node, item, section, size, highlight)
        // Empty wells must also consume presses so the panel's background does not drag.
        node.interactive = true
        if (!node.hitArea) node.hitArea = new Rectangle(0, 0, size, size)
        if (!item) return
        node.cursor = "grab"
        node.on("pointerdown", (event: InteractionEvent) => {
            if (this.held || (event.data.pointerType !== "touch" && event.data.button > 0)) return
            this.suppressed = undefined
            this.held = { source, pointerId: event.data.pointerId, start: event.data.global.clone(), moved: false }
            this.interaction.on("pointermove", this.move)
            this.interaction.on("pointerup", this.release)
            this.interaction.on("pointerupoutside", this.cancelPointer)
            this.interaction.on("pointercancel", this.cancelPointer)
            window.addEventListener("blur", this.cancel)
            event.stopPropagation()
        })
    }

    suppressClick = (node: Graphics) => this.suppressed === node
    get dragging() { return !!this.held?.moved }

    private optionFor = (section: ItemDropTarget, slot?: Slot) => {
        const source = this.held?.source
        if (!source || source === slot) return undefined
        if (slot?.item?.id && source.item?.itemSlotDropOptions?.[slot.item.id]) {
            return source.item.itemSlotDropOptions[slot.item.id]
        }
        if (source.section === section) return undefined
        return source.item?.itemDropOptions?.[section]
    }

    private slotAt = (event: InteractionEvent) => this.slots.find(slot => {
        const at = slot.node.toLocal(event.data.global)
        return slot.node.hitArea?.contains(at.x, at.y)
    })

    private targetAt = (event: InteractionEvent) => {
        const target = this.sections.find(section => {
            const at = section.node.toLocal(event.data.global)
            return section.bounds.contains(at.x, at.y)
        })
        return target?.section ?? this.slotAt(event)?.section
            ?? (this.isWorldDrop(event.data.global) ? "world" : undefined)
    }

    private move = (event: InteractionEvent) => {
        const held = this.held
        if (!held || held.pointerId !== event.data.pointerId) return
        if (!held.moved) {
            if (Math.hypot(event.data.global.x - held.start.x, event.data.global.y - held.start.y) < 6) return
            held.moved = true
            this.suppressed = held.source.node
            this.hideDetails()
            held.source.node.alpha = 0.4
            held.source.node.cursor = "grabbing"
            this.ghost = this.makeGhost(held.source.item!, held.source.size)
            this.ghost.alpha = 1
            this.ghost.interactive = false
            this.ghost.interactiveChildren = false
            this.layer.addChild(this.ghost)
        }
        const target = this.targetAt(event)
        const hoveredSlot = this.slotAt(event)
        this.slots.forEach(slot => slot.highlight(!!this.optionFor(slot.section, slot) && slot === hoveredSlot))
        this.sections.forEach(section => {
            // Area feedback stays steady across wells and gaps, even when only occupied
            // slots can accept the item (same-area replacements or a full ready bar).
            const acceptsDrop = !!this.optionFor(section.section) || this.slots.some(slot =>
                slot.section === section.section && !!this.optionFor(section.section, slot))
            this.highlightSection(section, section.section === target && acceptsDrop)
        })
        const at = this.layer.toLocal(event.data.global)
        this.ghost!.position.set(at.x - held.source.size / 2, at.y - held.source.size / 2)
    }

    private release = (event: InteractionEvent) => {
        if (!this.held || this.held.pointerId !== event.data.pointerId) return
        const target = this.targetAt(event)
        const item = this.held.source.item
        const option = this.held.moved && target ? this.optionFor(target, this.slotAt(event)) : undefined
        const at = target === "world" ? event.data.global.clone() : undefined
        this.cancel()
        if (option) this.select(item!, option, at)
    }

    private highlightSection = (section: Section, on: boolean) => {
        if (section.highlighted === on) return
        section.highlighted = on
        section.highlight(on)
    }

    private cancelPointer = (event: InteractionEvent) => {
        if (this.held?.pointerId === event.data.pointerId) this.cancel()
    }

    cancel = () => {
        if (this.held) {
            this.held.source.node.alpha = 1
            this.held.source.node.cursor = "grab"
        }
        this.held = undefined
        this.slots.forEach(slot => slot.highlight(false))
        this.sections.forEach(section => this.highlightSection(section, false))
        this.ghost?.parent?.removeChild(this.ghost)
        this.ghost?.destroy({ children: true })
        this.ghost = undefined
        this.interaction.off("pointermove", this.move)
        this.interaction.off("pointerup", this.release)
        this.interaction.off("pointerupoutside", this.cancelPointer)
        this.interaction.off("pointercancel", this.cancelPointer)
        window.removeEventListener("blur", this.cancel)
    }
}
